import { Link } from 'react-router-dom'
import { Section } from '@vanilla/ui'
import { ArrowLeft } from 'lucide-react'

export function VoxNotePrivacy() {
  return (
    <Section className="py-20">
      <div className="mx-auto max-w-3xl">
        <Link
          to="/labs/apps/voxnote"
          className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          VoxNote
        </Link>

        <h1 className="mb-2 text-4xl font-bold tracking-tight">Privacy Policy for VoxNote</h1>
        <p className="mb-8 text-sm text-muted-foreground">Last Updated: December 11, 2025</p>

        <div className="space-y-8 text-muted-foreground">
          <div>
            <h2 className="mb-4 text-xl font-semibold text-foreground">Overview</h2>
            <p className="mb-4">
              VoxNote is a voice notes application designed with privacy as a core principle.
              This Privacy Policy explains how we handle your information.
            </p>
            <p className="font-medium text-foreground">
              Our commitment: Your notes stay on your device. We don't operate servers,
              require accounts, or collect analytics.
            </p>
          </div>

          <div>
            <h2 className="mb-4 text-xl font-semibold text-foreground">Data Storage</h2>
            <h3 className="mb-2 font-medium text-foreground">What stays on your device</h3>
            <ul className="mb-4 list-inside list-disc space-y-1">
              <li>Note content - stored in local database</li>
              <li>Audio recordings - stored locally for playback</li>
              <li>Settings - stored in UserDefaults</li>
              <li>API keys (if provided) - stored in iOS Keychain (encrypted)</li>
            </ul>
            <h3 className="mb-2 font-medium text-foreground">What we sync</h3>
            <p className="mb-4">
              If you enable iCloud, notes sync to your personal iCloud account.
              Apple manages this sync; we have no access to your iCloud data.
            </p>
            <h3 className="mb-2 font-medium text-foreground">What we never collect</h3>
            <ul className="list-inside list-disc space-y-1">
              <li>Personal information (name, email, phone)</li>
              <li>Location data</li>
              <li>Device identifiers</li>
              <li>Usage analytics</li>
              <li>Crash reports</li>
              <li>Advertising identifiers</li>
            </ul>
          </div>

          <div>
            <h2 className="mb-4 text-xl font-semibold text-foreground">Voice Processing</h2>
            <p className="mb-4">When you record a voice note:</p>
            <ol className="mb-4 list-inside list-decimal space-y-1">
              <li>Audio is captured by your device's microphone</li>
              <li>Speech recognition runs entirely on-device using Apple's Speech framework and WhisperKit</li>
              <li>Audio is saved locally for playback (optional)</li>
              <li>Only the transcribed text becomes your note</li>
            </ol>
            <p className="font-medium text-foreground">No audio is ever sent to external servers.</p>
          </div>

          <div>
            <h2 className="mb-4 text-xl font-semibold text-foreground">AI Refinement</h2>
            <p className="mb-4">
              VoxNote offers AI-powered text refinement. How your data is handled depends on
              which provider you choose:
            </p>
            <h3 className="mb-2 font-medium text-foreground">Apple (Default)</h3>
            <ul className="mb-4 list-inside list-disc space-y-1">
              <li>All processing happens on your device</li>
              <li>No data leaves your device</li>
              <li>No internet connection required</li>
              <li>Available on iOS 26+ with Apple Intelligence</li>
            </ul>
            <h3 className="mb-2 font-medium text-foreground">OpenRouter (Optional)</h3>
            <p className="mb-2">If you choose to use OpenRouter for AI refinement:</p>
            <ul className="mb-4 list-inside list-disc space-y-1">
              <li>Note text is sent to OpenRouter's servers for processing</li>
              <li>OpenRouter routes to various AI models (GPT-4o-mini, Gemini, Claude)</li>
              <li>Your API key authenticates requests</li>
              <li>Subject to OpenRouter's Privacy Policy</li>
            </ul>
            <p>
              <strong className="text-foreground">You control this:</strong> OpenRouter is opt-in.
              You must provide your own API key. The default Apple provider keeps everything on-device.
            </p>
          </div>

          <div>
            <h2 className="mb-4 text-xl font-semibold text-foreground">Permissions</h2>
            <ul className="list-inside list-disc space-y-1">
              <li>Microphone - Record voice notes (required for voice)</li>
              <li>Speech Recognition - Convert speech to text (required for voice)</li>
            </ul>
            <p className="mt-2">Text note creation works without any permissions.</p>
          </div>

          <div>
            <h2 className="mb-4 text-xl font-semibold text-foreground">Data Security</h2>
            <ul className="list-inside list-disc space-y-1">
              <li>Notes are stored in a Core Data database on your device</li>
              <li>API keys are stored in the iOS Keychain (hardware-encrypted)</li>
              <li>Data is protected by your device's passcode/Face ID/Touch ID</li>
              <li>iCloud sync uses Apple's end-to-end encryption</li>
            </ul>
          </div>

          <div>
            <h2 className="mb-4 text-xl font-semibold text-foreground">Data Deletion</h2>
            <ul className="list-inside list-disc space-y-1">
              <li>Delete individual notes by swiping left in the notes list</li>
              <li>Uninstalling the app removes all local data</li>
              <li>iCloud data can be managed through iOS Settings</li>
            </ul>
          </div>

          <div>
            <h2 className="mb-4 text-xl font-semibold text-foreground">Children's Privacy</h2>
            <p>
              VoxNote does not knowingly collect information from children under 13.
              The app has no account system and requires no personal information.
            </p>
          </div>

          <div>
            <h2 className="mb-4 text-xl font-semibold text-foreground">Your Rights</h2>
            <p className="mb-2">Since all data is stored locally:</p>
            <ul className="list-inside list-disc space-y-1">
              <li><strong className="text-foreground">Access:</strong> Open the app to see your notes</li>
              <li><strong className="text-foreground">Deletion:</strong> Swipe to delete any note, or uninstall the app</li>
              <li><strong className="text-foreground">Portability:</strong> Share notes as text or images</li>
              <li><strong className="text-foreground">Control:</strong> Choose your AI provider</li>
            </ul>
          </div>

          <div>
            <h2 className="mb-4 text-xl font-semibold text-foreground">Contact</h2>
            <p>
              Questions about this Privacy Policy:{' '}
              <a href="mailto:info@khc.one" className="text-primary hover:underline">
                info@khc.one
              </a>
            </p>
          </div>

          <div className="rounded-lg bg-card p-6">
            <h2 className="mb-4 text-xl font-semibold text-foreground">Summary</h2>
            <ul className="space-y-2">
              <li><strong className="text-foreground">Data collection:</strong> None (local only)</li>
              <li><strong className="text-foreground">Analytics:</strong> None</li>
              <li><strong className="text-foreground">Advertising:</strong> None</li>
              <li><strong className="text-foreground">Account required:</strong> No</li>
              <li><strong className="text-foreground">Cloud sync:</strong> Optional (iCloud)</li>
              <li><strong className="text-foreground">Third-party sharing:</strong> Only if you opt-in to OpenRouter</li>
              <li><strong className="text-foreground">Audio sent to servers:</strong> Never</li>
            </ul>
            <p className="mt-4 font-medium text-foreground">
              VoxNote is designed so that your thoughts remain yours.
            </p>
          </div>
        </div>

        <p className="mt-16 text-center text-sm text-muted-foreground">
          © 2025 KHC Labs ·{' '}
          <Link to="/labs/apps/voxnote" className="text-primary hover:underline">
            VoxNote
          </Link>
        </p>
      </div>
    </Section>
  )
}
